import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["field"]
  static values = {
    token: String,
    endpoint: String,
    debounceMs: { type: Number, default: 600 },
    fieldName: String
  }

  connect() {
    // Detect all inputs inside the wrapper (for radio/checkbox groups)
    const fields = Array.from(this.element.querySelectorAll("input, textarea, select"))
    const nameGroups = fields.map(f => f.name)
    const hasDuplicates = nameGroups.some((n, i) => nameGroups.indexOf(n) !== i)

    // Use group of radios/checkboxes if multiple share the same name
    if (hasDuplicates) {
      const groupName = nameGroups.find((n, i) => nameGroups.indexOf(n) !== i)
      this._fields = fields.filter(f => f.name === groupName)
    } else {
      // Fallback: single field target
      this._fields = [this.hasFieldTarget ? this.fieldTarget : fields[0]].filter(Boolean)
    }

    this._lastSentValue = this._getCurrentValue()
    this._onInput = this._debounce(() => this.save(), this.debounceMsValue)

    this._ensureHiddenFlag()

    // Attach listeners
    this._fields.forEach((el) => {
      const eventType = el.type === "radio" || el.type === "checkbox" ? "change" : "input"
      el.addEventListener(eventType, this._onInput, { passive: true })
    })

    // React-based components
    window.addEventListener("input:change", this._handleReactChange)
    window.addEventListener("react-date-time:changed", this._handleReactDateChange)

    document.addEventListener("turbo:before-cache", this._onBeforeCache)
    window.addEventListener("pagehide", this._onPageHide)
  }

  disconnect() {
    this._fields.forEach((el) => {
      const eventType = el.type === "radio" || el.type === "checkbox" ? "change" : "input"
      el.removeEventListener(eventType, this._onInput)
    })

    window.removeEventListener("input:change", this._handleReactChange)
    window.removeEventListener("react-date-time:changed", this._handleReactDateChange)

    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    window.removeEventListener("pagehide", this._onPageHide)
  }

  _onBeforeCache = () => {
    this.save()
  }

  // Save before page unload
  _onPageHide = () => {
    this.save()
  }

  // ---- React SearchSelect Integration ----
   _handleReactChange = (e) => {
    const { value, id, selectedIds, name } = e.detail || {}
    const targetId =
      (id && (id.endsWith(this.fieldNameValue) || id.endsWith(this.fieldNameValue + '_input'))) ||
      (name && (name.endsWith('['+ this.fieldNameValue + ']') || name.endsWith(this.fieldNameValue)))

    if (!targetId) return

    let toSave
    if (Array.isArray(selectedIds)) {
      toSave = JSON.stringify(selectedIds)        // ← store as JSON array
    } else if (Array.isArray(value)) {
      toSave = JSON.stringify(value)
    } else {
      toSave = value ?? ''
    }

    this.saveValue(toSave)
  }

  // ---- React DatePicker Integration ----
  _handleReactDateChange = (e) => {
    const { formattedDate, id } = e.detail || {}
    const targetId = id.endsWith(this.fieldNameValue) || id.endsWith(this.fieldNameValue + '_input')
    if (id && targetId) {
      const value = formattedDate || ""
      this.saveValue(value)
    }
  }

  // ---- Public saving methods ----
  saveValue(value) {
    if (value === this._lastSentValue) return
    this._lastSentValue = value
    this._performSave(value)
  }

  save() {
    const value = this._getCurrentValue()
    if (value === this._lastSentValue) return
    this._lastSentValue = value
    this._performSave(value)
  }

  // ---- Core save routine ----
  async _performSave(value) {
    try {
      await fetch(this.endpointValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ token: this.tokenValue, value })
      })
      this._dispatch("saved")
    } catch (e) {
      console.warn("Autosave failed:", e)
    }
  }

  // ---- Helpers ----
  _getCurrentValue() {
    if (!this._fields || !this._fields.length) return ""
    const last = this._fields[this._fields.length - 1]

    if (last.type === "radio") {
      const checked = this._fields.find(r => r.checked)
      return checked ? checked.value : ""
    }

    if (last.type === "checkbox") {
      return this._fields.filter(c => c.checked).map(c => c.value).join(",")
    }

    return last.value || ""
  }

  _ensureHiddenFlag() {
    const form = this.element.closest("form")
    if (!form) return
    if (form.querySelector('input[name="_with_autosave_fields"]')) return
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = "_with_autosave_fields"
    input.value = "true"
    form.appendChild(input)
  }

  _headers() {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return {
      "Accept": "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": token || ""
    }
  }

  _debounce(fn, wait) {
    let t
    return (...args) => {
      clearTimeout(t)
      t = setTimeout(() => fn.apply(this, args), wait)
    }
  }

  _dispatch(name, detail = {}) {
    this.element.dispatchEvent(
      new CustomEvent(`autosave:${name}`, { detail, bubbles: true })
    )
  }
}
